import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { Checkbox, Flex, Select } from 'antd';
import {
  createFocusHandle,
  createOnBlur,
  FilterInputProps,
  FocusRef,
  GroupDescriptionOption,
  TextEllipsis,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './GroupDescriptionFilterInput.translations.json';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

const { addTranslations, t } = i18nNsFactory('manager-select-filter-input');
addTranslations(translations);

export interface GroupDescriptionFilterOptionType {
  key: string;
  label: string;
  options: GroupDescriptionOption[];
}

export interface GroupDescriptionFilterValueType {
  userFollowedDataObjectGroups?: boolean;
  groupDescription?: string | number;
}

export const GroupDescriptionFilterInput = forwardRef<
  FocusRef,
  FilterInputProps & {
    options: GroupDescriptionFilterOptionType[];
    placeholder?: string;
  }
>((props, ref) => {
  const { header, onBlur, options, placeholder } = props;
  const id = `${header.column.id}-filter-input`;

  useImperativeHandle(ref, () => createFocusHandle(id));
  const handleOnBlur = useMemo(
    () => createOnBlur<HTMLInputElement>(id, onBlur),
    [id, onBlur],
  );

  const filterValue = header.column.getFilterValue() as
    | GroupDescriptionFilterValueType
    | undefined;
  const { userFollowedDataObjectGroups, groupDescription } = useMemo(
    () => ({
      userFollowedDataObjectGroups:
        filterValue?.userFollowedDataObjectGroups ?? false,
      groupDescription: filterValue?.groupDescription ?? undefined,
    }),
    [filterValue],
  );

  const handleCheckboxChange = useCallback(
    (event: CheckboxChangeEvent) => {
      header.column.setFilterValue(() =>
        event.target.checked
          ? {
              groupDescription,
              userFollowedDataObjectGroups: true,
            }
          : undefined,
      );
    },
    [groupDescription, header.column],
  );

  const handleChange = useCallback(
    (groupDescription?: string | number) => {
      header.column.setFilterValue(() =>
        groupDescription == null
          ? undefined
          : {
              userFollowedDataObjectGroups: undefined,
              groupDescription: groupDescription.toString(),
            },
      );
    },
    [header.column],
  );

  return (
    <Flex onBlur={handleOnBlur} className={id} vertical gap="small">
      <Checkbox
        id={`${id}-checkbox`}
        checked={userFollowedDataObjectGroups}
        onChange={handleCheckboxChange}
        disabled={!!groupDescription}
      >
        <TextEllipsis tabIndex={-1}>
          {t('userFollowedDataObjectGroups')}
        </TextEllipsis>
      </Checkbox>
      <Select
        id={id}
        allowClear
        className={id}
        defaultOpen
        styles={{
          popup: { root: { width: '300px' } },
        }}
        filterOption={(input, option) =>
          Boolean(option?.label?.toLowerCase().includes(input.toLowerCase()))
        }
        placeholder={placeholder}
        options={options}
        onBlur={handleOnBlur}
        onChange={handleChange}
        showSearch
        value={groupDescription}
        disabled={userFollowedDataObjectGroups}
      />
    </Flex>
  );
});
